import debounce from 'lodash/debounce';
import React, { useCallback, useLayoutEffect, useRef, useState } from 'react';

export const useMaxLines = (
  maxLines: number,
  isOpen: boolean,
  children: string,
  setText: React.Dispatch<React.SetStateAction<string>>,
  setRulerText: React.Dispatch<React.SetStateAction<string>>,
) => {
  const [truncated, setTruncated] = useState(false);
  const readMoreRef = useRef<HTMLDivElement | null>(null);
  const textRulerRef = useRef<HTMLDivElement | null>(null);
  const ellipsesRulerRef = useRef<HTMLDivElement | null>(null);
  const getButtonWidth = () => ellipsesRulerRef.current?.offsetWidth ?? 0;
  const getAllowedWidth = () => readMoreRef.current?.clientWidth ?? 0;

  const delay = (ms: number) => new Promise(resolve => setTimeout(resolve, ms));

  const createMaxLines = useCallback(async () => {
    let usedWords = '';
    let line = '';

    const wordArray: string[] = children
      .trim()
      .split(' ')
      .filter(c => c !== '');

    // you have to check by line, because when a word is too long for that line, all characters will jump to next line
    // if checking the last line, we need to take into account the width of the button
    const arrayLen = wordArray.length;
    let lineCount = 0;
    setRulerText('');

    while (lineCount < maxLines) {
      for (let i = 0; i < arrayLen; i += 1) {
        const word = wordArray.shift();

        if (word) {
          // account for new line characters
          const newLineCount = word.lastIndexOf('\n') + 1;
          const currLines = lineCount + newLineCount;

          // if too many lines, break and add the last line back to the wordArray
          if (currLines >= maxLines) {
            wordArray.unshift(word);
            break;
          }

          const previousLine = usedWords;
          if (i === 0 && currLines === 0) {
            setRulerText(text => text + word);
            usedWords += word;
          } else {
            setRulerText(text => `${text} ${word}`);
            usedWords += ` ${word}`;
          }

          // This "delay" is needed for these reasons: https://dev.to/bytebodger/updating-react-state-inside-loops-2dbf
          await delay(0);

          // if the line is too long, use the previous line and break
          // and add the last line back to the wordArray;
          if (
            textRulerRef.current?.offsetWidth &&
            ((currLines < maxLines - 1 && textRulerRef.current?.offsetWidth >= getAllowedWidth()) ||
              // for the last line, take into account the width of the button
              (currLines === maxLines - 1 &&
                textRulerRef.current?.offsetWidth >= getAllowedWidth() - getButtonWidth()))
          ) {
            setRulerText(previousLine);
            usedWords = previousLine;
            wordArray.unshift(word);
            break;
          }

          // if we had new lines, make sure they get added to the running line count
          if (newLineCount) {
            lineCount = currLines;
          }
        }
      }

      lineCount++;
      line += usedWords;
      setRulerText('');
      usedWords = '';
      if (wordArray.length > 0 && lineCount == maxLines) {
        // We've used all our lines and there are more words. This means we are truncating.
        setTruncated(true);
      } else if (wordArray.length == 0) {
        // We've reached the end of the words and didn't need to truncate.
        setTruncated(false);
      }
    }
    setRulerText('');
    setText(line);
  }, [children, maxLines, setRulerText, setText, setTruncated]);

  useLayoutEffect(() => {
    const debouncedCreateMaxLines = debounce(createMaxLines, 300);
    if (maxLines) {
      window.addEventListener('resize', debouncedCreateMaxLines);
      if (isOpen) {
        setText(children);
      } else {
        createMaxLines();
      }
    }
    return () => {
      if (maxLines) window.removeEventListener('resize', debouncedCreateMaxLines);
    };
  }, [maxLines, isOpen, children, setText, createMaxLines]);

  return {
    readMoreRef,
    textRulerRef,
    ellipsesRulerRef,
    truncated,
  };
};
